// React
import { forwardRef, HTMLProps, memo, ReactNode } from "react"

// UI
import { cn } from "@/lib/utils"

const BACKGROUND_COLORS = {
	white: "bg-white",
	light: "bg-gray-50",
	gray: "bg-gray-100",
	dark: "bg-gray-200",
}

type CardVariants = keyof typeof BACKGROUND_COLORS
export interface CardProps extends Omit<HTMLProps<HTMLDivElement>, "ref"> {
	className?: string
	variant?: CardVariants
}

export const Card = ({ variant, children, ...rest }: CardProps) => {
	return (
		<CardWrapper variant={variant} {...rest}>
			<CardBody>{children}</CardBody>
		</CardWrapper>
	)
}

export const CardWrapper = memo(
	({ className = "", variant = "white", ...props }: CardProps) => (
		<div
			className={cn(
				BACKGROUND_COLORS[variant],
				"rounded-xs shadow sm:rounded-lg",
				className,
			)}
			{...props}
		/>
	),
)
CardWrapper.displayName = "CardWrapper"

export const CardHeader = memo(
	({ className = "", variant = "white", ...props }: CardProps) => (
		<div
			className={cn(
				BACKGROUND_COLORS[variant],
				"px-4 py-5 sm:p-6",
				className,
			)}
			{...props}
		/>
	),
)
CardHeader.displayName = "CardHeader"
export const CardBody = forwardRef<HTMLDivElement, Omit<CardProps, "variant">>(
	({ className = "", ...props }, ref) => (
		<div
			className={cn("px-4 py-5 sm:p-6", className)}
			{...props}
			ref={ref}
		/>
	),
)
CardBody.displayName = "CardBody"

interface CardFooterProps {
	className?: string
	children?: ReactNode
}

export const CardFooter = ({ className = "", ...props }: CardFooterProps) => (
	<div
		className={cn(
			"rounded-b-sm bg-gray-50 px-4 py-3 sm:rounded-b-lg sm:px-6",
			className,
		)}
		{...props}
	/>
)
